import { z } from "zod";

/* -------------------------
   Zod Schema for Form Validation
-------------------------- */
// Base schema with optional verificationCode
export const screenSchema = z.object({
  screenName: z.string().min(2, "Screen name is required"),
  debugFlag: z.boolean().optional(),
  resetFlag: z.boolean().optional(),
  status: z.boolean().optional(),
  layout: z.string().optional(),
  screenSaver: z.string().optional(),
  address: z.string().min(1, "Address is required"),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  regionId: z.string().optional(),
  restaurantId: z.string().optional(),
  tickerId: z.string().optional(),
  widgetId: z.string().optional(),
  primeEndTime: z.string().optional(),
  primeStartTime: z.string().optional(),
  orientation: z.string().optional(),
  tagIds: z.array(z.string()).optional(),
  verificationCode: z.string().optional(),
});

// Schema for creating (verificationCode required)
export const createScreenSchema = screenSchema.extend({
  verificationCode: z.string().min(1, "Verification code is required"),
});

export type ScreenFormData = z.infer<typeof screenSchema>;

/* -------------------------
   GraphQL Response Interfaces
-------------------------- */

export interface Region {
  id: string;
  name: string;
  city?: City;
}

export interface City {
  id: string;
  name: string;
}

export interface Restaurant {
  id: string;
  code?: string;
  name: string;
  description?: string;
}

export interface Playlist {
  id: string;
  name: string;
  type?: string;
  duration: number;
  isActive: boolean;
  tenantId?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

// Full Screen type (matches GraphQL)
export interface User {
  id: string;
  name: string;
}

export interface Device {
  id: string;
  /** True for Raspberry Pi players — gates Android-only actions like Unassign. */
  isPi?: boolean;
  [key: string]: unknown; // Device type is not fully defined in GraphQL schema
}

export interface Screen {
  id: string;
  screenName: string;
  debugFlag: boolean;
  resetFlag: boolean;
  lastResetAt?: string;
  status?: boolean;
  lastOnAt?: string;
  layout?: string;
  screenSaver?: string;
  address: string;
  latitude?: number;
  longitude?: number;
  regionId?: string;
  region?: Region;
  restaurantId?: string;
  restaurant?: Restaurant;
  clientPlaylist?: Playlist;
  tenantId: string;
  tickerId?: string;
  widgetId?: string;
  primeEndTime?: string;
  primeStartTime?: string;
  orientation?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  tags?: string;
  tagIds: string[];
  clients: User[];
  isScheduled: boolean;
  device?: Device;
  verificationCode?: string;
  anydeskId?: string;
  nowPlayingCreativeId?: string;
  nowPlayingCreativeName?: string;
  nowPlayingType?: string;
  nowPlayingStartedAt?: string;
  nowPlayingDuration?: number;
  nowPlayingFileUrl?: string;
}

export interface ScreenResult {
  screens: Screen[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// Per-column "contains" search (matches backend ScreenFilterField enum).
export type ScreenFilterField =
  | "SCREEN_NAME"
  | "CUSTOMER_ID"
  | "RESTAURANT_NAME"
  | "CITY"
  | "REGION"
  | "DEVICE_CODE"
  | "STATUS"
  | "ORIENTATION"
  | "LAST_ON_DATE"
  | "LAST_ON_TIME";

export interface ScreenColumnFilter {
  field: ScreenFilterField;
  value: string;
}

// Server-side sort (backend `sortBy: ScreenFilterField`, `sortOrder:
// ScreenSortOrder`). Sort keys are the same as the column filter keys.
export type ScreenSortOrder = "ASC" | "DESC";

// Input for create mutation (matches CreateScreenInput)
export interface CreateScreenInput {
  screenName: string;
  debugFlag?: boolean;
  resetFlag?: boolean;
  status?: boolean;
  layout?: string;
  screenSaver?: string;
  address: string;
  latitude?: number;
  longitude?: number;
  regionId?: string;
  restaurantId?: string;
  tickerId?: string;
  widgetId?: string;
  primeEndTime?: string;
  primeStartTime?: string;
  orientation?: string;
  tagIds?: string[];
  verificationCode: string;
}

// Input for update mutation (matches UpdateScreenInput)
export interface UpdateScreenInput {
  screenName?: string;
  debugFlag?: boolean;
  resetFlag?: boolean;
  status?: boolean;
  layout?: string;
  screenSaver?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  regionId?: string;
  restaurantId?: string;
  tickerId?: string;
  widgetId?: string;
  primeEndTime?: string;
  primeStartTime?: string;
  orientation?: string;
  tagIds?: string[];
  verificationCode?: string;
}

// For getScreensByCityWithRegions query
export interface CityWithRegions {
  city: string;
  regions: RegionWithScreens[];
}

// Alias for backward compatibility
export type ScreenInput = CreateScreenInput | UpdateScreenInput;

export interface RegionWithScreens {
  regionName: string;
  screens: ScreenSummary[];
}

export interface ScreenSummary {
  screenId: string;
  screenName: string;
}
